import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FinanceProductType } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import CascaderField from '../../../../components/fields/CascaderField';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { useFinanceProductDetailContext } from '../FinanceProductDetailContext';
import { includesFinancing } from '../shared/groupings';
import useFinanceProductOptions from '../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from './index';
import YesNoField from './ParametersForm/YesNoField';

const colSpan = { lg: 8, xs: 24 };

export type CalculationFormProps = {
    availableFPType?: string[];
    disabled?: boolean;
};

const CalculationForm = ({ availableFPType, disabled = false }: CalculationFormProps) => {
    const { t } = useTranslation(['financeProductDetails']);
    const company = useCompany(true);
    const { bank } = useFinanceProductDetailContext();

    const { values } = useFormikContext<FinanceProductFormValues>();
    const { type } = values;

    const { financeProductTypeOptions, deriveMethodOptions, paymentModeOptions, calculationModeOptions } =
        useFinanceProductOptions(bank?.module?.company?.id, company?.countryCode);

    const availableFinanceProductTypeOptions = useMemo(() => {
        const availableFPList = [];
        financeProductTypeOptions.forEach(fp => {
            const children = fp.children.filter(children => availableFPType.includes(children.value));
            if (children.length) {
                availableFPList.push({
                    ...fp,
                    children,
                });
            }
        });

        return availableFPList;
    }, [availableFPType, financeProductTypeOptions]);

    const isFinancing = includesFinancing(type);

    return (
        <Row gutter={[16, 16]}>
            <Col {...colSpan}>
                <CascaderField
                    {...t('financeProductDetails:fields.financeProductType', { returnObjects: true })}
                    disabled={disabled}
                    name="type"
                    options={availableFinanceProductTypeOptions}
                    required
                    showSearch
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.SelectField
                    {...t('financeProductDetails:fields.deriveMethod', { returnObjects: true })}
                    name="deriveMethod"
                    options={deriveMethodOptions}
                    disabled
                    required
                    showSearch
                />
            </Col>

            {isFinancing && (
                <>
                    <Col {...colSpan}>
                        <FormFields.SelectField
                            {...t('financeProductDetails:fields.paymentMode', { returnObjects: true })}
                            disabled={disabled}
                            name="payment.mode"
                            options={paymentModeOptions}
                            required
                            showSearch
                        />
                    </Col>

                    <Col {...colSpan}>
                        <YesNoField
                            {...t('financeProductDetails:fields.paymentModeViewable', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="payment.isViewable"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <YesNoField
                            {...t('financeProductDetails:fields.paymentModeEditable', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="payment.isEditable"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SelectField
                            {...t('financeProductDetails:fields.calculationMode', { returnObjects: true })}
                            disabled={type === FinanceProductType.UcclLeasing || disabled}
                            name="calculationMode"
                            options={calculationModeOptions}
                            required
                            showSearch
                        />
                    </Col>
                </>
            )}
            {type === FinanceProductType.HirePurchaseWithBalloonGfv && (
                <Col {...colSpan}>
                    <FormFields.InputNumberField
                        {...t('financeProductDetails:fields.reducedMonthlyPayment', { returnObjects: true })}
                        disabled={disabled}
                        max={100}
                        min={0}
                        name="balloonGfv.reducedMonthlyPayment"
                        required
                    />
                </Col>
            )}
        </Row>
    );
};

export default CalculationForm;
